import { formatRelative } from './accountsStaleLabels'
import type { GroupHealth } from './connectionGroups'

export type StatusTone = 'ok' | 'stale' | 'reauth' | 'neutral'

export type StatusInfo = { tone: StatusTone; text: string; title: string }

/**
 * `accountHealth()` is the ONE interpreter of connection health, and
 * `connectionGroups.ts`'s `healthFor` reads a group through it —
 * this only maps the resulting five kinds onto a row's short text, dot tone and long form. Rule
 * 11: the pill's text differs per state, never the dot colour alone.
 *
 * Exported for `AccountCard.tsx` (M5 Ticket 3 gate, blocking 3) — the card's
 * overlay covers the pill, so its `title` never fires on hover and has no
 * keyboard/touch path either. `AccountCard` reads `.title` from here to
 * expose the SAME long form as real (`sr-only`) text, rather than deriving
 * a second copy of this mapping.
 */
export function statusInfoOf(health: GroupHealth | null): StatusInfo {
  if (health === null) {
    return { tone: 'neutral', text: 'Setting up', title: 'Setting up — no accounts synced yet' }
  }
  switch (health.kind) {
    case 'ok':
      return {
        tone: 'ok',
        text: 'Synced',
        title: health.lastSyncedAt ? `Last synced ${formatRelative(health.lastSyncedAt)}` : 'Synced',
      }
    case 'manual':
      return { tone: 'neutral', text: 'Manual', title: 'Updated by you' }
    case 'stale':
      return { tone: 'stale', text: 'Stale', title: `Last synced ${formatRelative(health.since)}` }
    case 'reauth':
      return {
        tone: 'reauth',
        text: 'Reconnect',
        title: `${health.institution ?? 'This connection'} needs reconnecting`,
      }
    // `connection_status: "disconnected"` — the user removed it, or its
    // provider did. A calm fact, not a fault, and never the live `reauth`
    // ("needs reconnecting") case, which is a broken connection that still
    // exists. Whether Reconnect is offered is `reconnectable`, not this text.
    case 'disconnected':
      return {
        tone: 'neutral',
        text: 'Disconnected',
        title: 'Disconnected — history kept, no longer updating. You can connect this bank again later.',
      }
  }
}

/**
 * Exported for `AccountCard.tsx` (M5 Ticket 3, #425 §A5) — "fold `RowHealthNote`
 * into the status pill" means the CARD's pill is this same component, not a
 * second one with its own tone/text mapping that could drift from this one.
 * `statusInfoOf` above is ALSO exported (gate finding, blocking 3, this
 * ticket's re-gate) — `AccountCard` reads its `.title` directly to render
 * the same long form as real `sr-only` text, which needs the raw
 * tone/text/title triple, not just the rendered pill this component gives.
 */
export function StatusPill({ health }: { health: GroupHealth | null }) {
  const info = statusInfoOf(health)
  return (
    <span
      className={`institutions-panel__status institutions-panel__status--${info.tone}`}
      title={info.title}
    >
      <span className="institutions-panel__dot" aria-hidden="true" />
      {info.text}
    </span>
  )
}
